import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/base/SEO';

export default function BusinessesPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://braincommerce.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Hybrid Workforce Solutions",
    "provider": {
      "@type": "Organization",
      "name": "BrainCommerce"
    },
    "areaServed": {
      "@type": "Country",
      "name": "South Korea"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Workforce Solutions",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Manpower Solutions",
            "description": "Global workforce solutions including recruitment, talent management, and workforce optimization."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI Integration Layer",
            "description": "AI-powered workforce optimization, intelligent task allocation, and predictive analytics."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "BC Robotics",
            "description": "Advanced robotics solutions for warehouse automation, manufacturing, and logistics."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Maintenance & Service",
            "description": "Professional maintenance and service for robotics and smart factory equipment."
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Our Businesses - Manpower, AI, Robotics & Maintenance | BrainCommerce"
        description="Explore BrainCommerce's integrated ecosystem: Manpower workforce solutions, AI integration layer, BC Robotics automation, and professional maintenance services for smart factories."
        keywords="Manpower solutions, BC Robotics, AI workforce, robotics maintenance, smart factory, warehouse automation, workforce integration"
        canonical={`${siteUrl}/businesses`}
        schema={schema}
      />
      
      <div className="min-h-screen bg-white pt-16 md:pt-20">
        {/* Overview Section */}
        <section className="py-12 md:py-24 px-4 md:px-8 lg:px-32">
          <div className="max-w-4xl mx-auto text-center mb-12 md:mb-20">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-extralight text-[#1a1a1a] mb-6 md:mb-8 tracking-tight">
              {t('businesses.overview.title')}
            </h1>
            <p className="text-base md:text-lg text-[#4a4a4a] leading-relaxed">
              {t('businesses.overview.description')}
            </p>
          </div>

          {/* Ecosystem Diagram - Mobile Optimized */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 mb-12 md:mb-20">
            <div className="bg-white rounded-lg p-6 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)] w-full md:w-64 text-center">
              <i className="ri-user-line text-4xl md:text-5xl text-[#1a1a1a] mb-3 md:mb-4"></i>
              <h3 className="text-lg md:text-xl font-semibold text-[#1a1a1a]">{t('businesses.overview.manpower')}</h3>
            </div>
            <i className="ri-arrow-down-line md:ri-arrow-right-line text-2xl md:text-3xl text-[#4a4a4a]"></i>
            <div className="bg-gradient-to-b from-white to-[#e8f2f7] rounded-lg p-6 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)] w-full md:w-64 text-center">
              <i className="ri-brain-line text-4xl md:text-5xl text-[#1a1a1a] mb-3 md:mb-4"></i>
              <h3 className="text-lg md:text-xl font-semibold text-[#1a1a1a]">{t('businesses.overview.aiLayer')}</h3>
            </div>
            <i className="ri-arrow-down-line md:ri-arrow-right-line text-2xl md:text-3xl text-[#4a4a4a]"></i>
            <div className="bg-white rounded-lg p-6 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)] w-full md:w-64 text-center">
              <i className="ri-robot-line text-4xl md:text-5xl text-[#1a1a1a] mb-3 md:mb-4"></i>
              <h3 className="text-lg md:text-xl font-semibold text-[#1a1a1a]">{t('businesses.overview.robotics')}</h3>
            </div>
          </div>
        </section>

        {/* Manpower Section */}
        <section className="py-12 md:py-20 px-4 md:px-8 lg:px-32 bg-[#fafafa]">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-4xl font-light text-[#1a1a1a] mb-4 md:mb-6 tracking-tight">
                  {t('businesses.manpower.title')}
                </h2>
                <p className="text-sm md:text-base text-[#4a4a4a] leading-relaxed mb-6 md:mb-8">
                  {t('businesses.manpower.description')}
                </p>
                <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.manpower.service1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.manpower.service2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.manpower.service3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.manpower.service4')}</span>
                  </li>
                </ul>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 md:px-8 py-2 md:py-3 border-2 border-[#1a1a1a] text-[#1a1a1a] rounded-full hover:bg-[#1a1a1a] hover:text-white transition-all whitespace-nowrap cursor-pointer text-sm md:text-base"
                >
                  {t('businesses.manpower.cta')}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden">
                <img 
                  src="https://public.readdy.ai/ai/img_res/ab9a942c8eeba337e4c2f5f55e0cfd94.jpg"
                  alt="Manpower"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* AI Layer Section */}
        <section className="py-12 md:py-20 px-4 md:px-8 lg:px-32">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 md:order-1 w-full h-64 md:h-96 rounded-lg overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20artificial%20intelligence%20neural%20networks%20and%20data%20streams%20in%20clean%20minimalist%20style%20with%20soft%20blue%20and%20white%20tones%20showing%20intelligent%20systems%20connecting%20and%20optimizing%20workflows%20in%20modern%20technological%20environment&width=800&height=600&seq=ai001&orientation=landscape"
                  alt="AI Layer"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="order-1 md:order-2">
                <h2 className="text-2xl md:text-4xl font-light text-[#1a1a1a] mb-4 md:mb-6 tracking-tight">
                  {t('businesses.ai.title')}
                </h2>
                <p className="text-sm md:text-base text-[#4a4a4a] leading-relaxed mb-6 md:mb-8">
                  {t('businesses.ai.description')}
                </p>
                <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.ai.service1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.ai.service2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.ai.service3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.ai.service4')}</span>
                  </li>
                </ul>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 md:px-8 py-2 md:py-3 border-2 border-[#1a1a1a] text-[#1a1a1a] rounded-full hover:bg-[#1a1a1a] hover:text-white transition-all whitespace-nowrap cursor-pointer text-sm md:text-base"
                >
                  {t('businesses.ai.cta')}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* BC Robotics Section */}
        <section className="py-12 md:py-20 px-4 md:px-8 lg:px-32 bg-[#fafafa]">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-4xl font-light text-[#1a1a1a] mb-4 md:mb-6 tracking-tight">
                  {t('businesses.robotics.title')}
                </h2>
                <p className="text-sm md:text-base text-[#4a4a4a] leading-relaxed mb-6 md:mb-8">
                  {t('businesses.robotics.description')}
                </p>
                <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.robotics.service1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.robotics.service2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.robotics.service3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.robotics.service4')}</span>
                  </li>
                </ul>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 md:px-8 py-2 md:py-3 border-2 border-[#1a1a1a] text-[#1a1a1a] rounded-full hover:bg-[#1a1a1a] hover:text-white transition-all whitespace-nowrap cursor-pointer text-sm md:text-base"
                >
                  {t('businesses.robotics.cta')}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Advanced%20industrial%20robotic%20arms%20and%20autonomous%20mobile%20robots%20working%20in%20clean%20modern%20warehouse%20facility%20with%20white%20walls%20and%20organized%20layout%20showing%20precision%20automation%20and%20robotic%20workforce%20excellence%20in%20minimalist%20sophisticated%20environment&width=800&height=600&seq=robotics001&orientation=landscape"
                  alt="BC Robotics"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Maintenance & Service Section */}
        <section className="py-12 md:py-20 px-4 md:px-8 lg:px-32">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 md:order-1 w-full h-64 md:h-96 rounded-lg overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20technician%20engineer%20performing%20maintenance%20service%20on%20advanced%20industrial%20robotic%20equipment%20and%20smart%20factory%20machinery%20in%20clean%20modern%20facility%20with%20white%20walls%20showing%20precision%20technical%20work%20diagnostic%20tools%20and%20preventive%20maintenance%20excellence%20in%20minimalist%20sophisticated%20environment&width=800&height=600&seq=maintenance001&orientation=landscape"
                  alt="Maintenance & Service"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="order-1 md:order-2">
                <h2 className="text-2xl md:text-4xl font-light text-[#1a1a1a] mb-4 md:mb-6 tracking-tight">
                  {t('businesses.maintenance.title')}
                </h2>
                <p className="text-sm md:text-base text-[#4a4a4a] leading-relaxed mb-6 md:mb-8">
                  {t('businesses.maintenance.description')}
                </p>
                <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.maintenance.service1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.maintenance.service2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.maintenance.service3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                    <span className="text-sm md:text-base text-[#4a4a4a]">{t('businesses.maintenance.service4')}</span>
                  </li>
                </ul>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 md:px-8 py-2 md:py-3 border-2 border-[#1a1a1a] text-[#1a1a1a] rounded-full hover:bg-[#1a1a1a] hover:text-white transition-all whitespace-nowrap cursor-pointer text-sm md:text-base"
                >
                  {t('businesses.maintenance.cta')}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
